import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  BLOOD_GLUCOSE_FILTERS,
  BLOOD_GLUCOSE_COLORS,
  BLOOD_PRESSURE_COLORS,
} from "@/components/charts";

interface BloodGlucoseFiltersProps {
  selectedFilters: string[];
  onFilterChange: (filters: string[]) => void;
  isDayView: boolean;
}

export const BloodGlucoseFilters = ({
  selectedFilters,
  onFilterChange,
  isDayView,
}: BloodGlucoseFiltersProps) => {
  const toggleFilter = (filter: string) => {
    if (filter === "All") {
      onFilterChange(["All"]);
    } else {
      const newFilters = selectedFilters.includes("All")
        ? [filter]
        : selectedFilters.includes(filter)
          ? selectedFilters.filter((f) => f !== filter)
          : [...selectedFilters, filter];

      onFilterChange(newFilters.length === 0 ? ["All"] : newFilters);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {BLOOD_GLUCOSE_FILTERS.map((filter) => (
        <Button
          key={filter}
          variant={selectedFilters.includes(filter) ? "default" : "outline"}
          size="sm"
          onClick={() => toggleFilter(filter)}
          className={cn(
            "rounded-full px-3 py-1 h-8 text-xs font-medium transition-all",
            selectedFilters.includes(filter)
              ? "text-white border-transparent"
              : "bg-white text-muted-foreground border-border hover:bg-muted hover:text-muted-foreground"
          )}
          style={
            selectedFilters.includes(filter) && filter !== "All"
              ? { backgroundColor: BLOOD_GLUCOSE_COLORS[filter] }
              : selectedFilters.includes(filter) && filter === "All"
                ? { backgroundColor: "oklch(var(--foreground))" }
                : {}
          }
        >
          <div className="flex items-center gap-1.5">
            {filter !== "All" && (
              <div
                className="w-2 h-2 rounded-full"
                style={{
                  backgroundColor: selectedFilters.includes(filter)
                    ? "white"
                    : BLOOD_GLUCOSE_COLORS[filter],
                }}
              />
            )}
            {filter}
          </div>
        </Button>
      ))}
    </div>
  );
};

interface BloodPressureToggleProps {
  isSystolic: boolean;
  onToggle: (isSystolic: boolean) => void;
}

export const BloodPressureToggle = ({
  isSystolic,
  onToggle,
}: BloodPressureToggleProps) => {
  return (
    <div className="flex items-center gap-3">
      <Button
        variant={isSystolic ? "default" : "outline"}
        size="sm"
        onClick={() => onToggle(true)}
        className={cn(
          "rounded-full px-4 py-2 h-9 text-sm font-medium transition-all",
          isSystolic
            ? "text-white border-transparent"
            : "bg-white text-muted-foreground border-border hover:bg-muted"
        )}
        style={
          isSystolic ? { backgroundColor: BLOOD_PRESSURE_COLORS.systolic } : {}
        }
      >
        <div className="flex items-center gap-2">
          <div
            className="w-2.5 h-2.5 rounded-full"
            style={{
              backgroundColor: isSystolic
                ? "white"
                : BLOOD_PRESSURE_COLORS.systolic,
            }}
          />
          Systolic
        </div>
      </Button>

      <Button
        variant={!isSystolic ? "default" : "outline"}
        size="sm"
        onClick={() => onToggle(false)}
        className={cn(
          "rounded-full px-4 py-2 h-9 text-sm font-medium transition-all",
          !isSystolic
            ? "text-white border-transparent"
            : "bg-white text-muted-foreground border-border hover:bg-muted"
        )}
        style={
          !isSystolic
            ? { backgroundColor: BLOOD_PRESSURE_COLORS.diastolic }
            : {}
        }
      >
        <div className="flex items-center gap-2">
          <div
            className="w-2.5 h-2.5 rounded-full"
            style={{
              backgroundColor: !isSystolic
                ? "white"
                : BLOOD_PRESSURE_COLORS.diastolic,
            }}
          />
          Diastolic
        </div>
      </Button>
    </div>
  );
};
